import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { isLoggedIn, reorder, reorderGuest } from '@theme/wishlist';

const CELL = '[data-wishlist-item]';
const HANDLE = '[data-wishlist-handle]';

class WishlistSortable extends Component {
  #grabbed = null;
  #dragging = null;
  #snapshot = null;
  #moving = false;
  #persisting = Promise.resolve();
  #persistBusy = false;

  /** The order the server last confirmed. Held across a burst of moves, not reset per click. */
  #markSnapshot() {
    if (!this.#persistBusy) this.#snapshot = this.#keys();
  }
  #abort = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    document.addEventListener(ThemeEvents.wishlistUpdate, this.#sync, { signal });

    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  get #list() {
    return this.querySelector('.ca-wishlist__grid');
  }

  #cells() {
    return [...this.querySelectorAll(CELL)];
  }

  #keys() {
    return this.#cells().map((cell) => cell.dataset.wishlistItem);
  }

  #position(cell) {
    return this.#cells().indexOf(cell) + 1;
  }

  #title(cell) {
    return cell?.querySelector('[data-wishlist-handle]')?.dataset.title ?? '';
  }

  #say(template, cell) {
    const region = this.querySelector('[data-wishlist-live]');
    if (!region || !template) return;

    const message = template
      .replace('[title]', this.#title(cell))
      .replace('[position]', String(this.#position(cell)))
      .replace('[total]', String(this.#cells().length));

    // Cleared first: a region re-set to the text it already holds is not re-announced.
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  #sync = () => {
    const cells = this.#cells();
    this.dataset.enabled = String(cells.length > 1);

    cells.forEach((cell, index) => {
      // aria-disabled rather than disabled: the control keeps focus at the edges.
      cell
        .querySelector('[data-wishlist-move-up]')
        ?.setAttribute('aria-disabled', String(index === 0));
      cell
        .querySelector('[data-wishlist-move-down]')
        ?.setAttribute('aria-disabled', String(index === cells.length - 1));
    });
  };

  // Re-inserting a node blurs what is focused inside it, which would drop the carried item.
  #reinsert(cell, reference, after) {
    this.#moving = true;
    reference.parentNode.insertBefore(cell, after ? reference.nextSibling : reference);
    this.#moving = false;
  }

  #move(cell, delta) {
    const cells = this.#cells();
    const target = cells[cells.indexOf(cell) + delta];
    if (!target) return false;

    this.#reinsert(cell, target, delta > 0);
    return true;
  }

  /** The single-pointer path, required by WCAG 2.5.7 and by VoiceOver/TalkBack. */
  #onClick = (event) => {
    if (this.dataset.enabled !== 'true') return;

    const button = event.target.closest('[data-wishlist-move-up], [data-wishlist-move-down]');
    if (!button || !this.contains(button)) return;

    event.preventDefault();
    if (button.getAttribute('aria-disabled') === 'true') return;

    const cell = button.closest(CELL);
    if (!cell) return;

    this.#markSnapshot();
    if (!this.#move(cell, button.hasAttribute('data-wishlist-move-up') ? -1 : 1)) return;

    this.#sync();
    button.focus();
    this.#say(this.dataset.movedText, cell);
    void this.#persist();
  };

  #onPointerDown = (event) => {
    if (event.button !== 0 || this.dataset.enabled !== 'true') return;

    const handle = event.target.closest(HANDLE);
    if (!handle || !this.contains(handle)) return;
    if (this.#grabbed) this.#release(this.#grabbed, true);

    const cell = handle.closest(CELL);
    if (!cell) return;

    event.preventDefault();
    this.#markSnapshot();
    this.#dragging = cell;
    cell.classList.add('is-dragging');

    // Listen on the document: pointer capture is released when a node moves in the DOM.
    const { signal } = this.#abort;
    document.addEventListener('pointermove', this.#onPointerMove, { signal });
    document.addEventListener('pointerup', this.#onPointerUp, { signal });
    document.addEventListener('pointercancel', this.#onPointerUp, { signal });
  };

  #onPointerMove = (event) => {
    const cell = this.#dragging;
    if (!cell) return;

    const over = document.elementFromPoint(event.clientX, event.clientY)?.closest(CELL);
    if (!over || over === cell || !this.contains(over)) return;

    const cells = this.#cells();
    this.#reinsert(cell, over, cells.indexOf(over) > cells.indexOf(cell));
  };

  #onPointerUp = () => {
    const cell = this.#dragging;
    if (!cell) return;

    document.removeEventListener('pointermove', this.#onPointerMove);
    document.removeEventListener('pointerup', this.#onPointerUp);
    document.removeEventListener('pointercancel', this.#onPointerUp);

    cell.classList.remove('is-dragging');
    this.#dragging = null;
    this.#sync();
    this.#say(this.dataset.droppedText, cell);
    void this.#persist();
  };

  #onKeyDown = (event) => {
    const handle = event.target.closest?.(HANDLE);
    if (!handle || !this.contains(handle) || this.dataset.enabled !== 'true') return;

    const cell = handle.closest(CELL);
    if (!cell) return;

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      if (this.#grabbed === cell) this.#release(cell);
      else this.#grab(cell, handle);
      return;
    }

    if (event.key === 'Escape' && this.#grabbed === cell) {
      event.preventDefault();
      // Release BEFORE restoring, or the resulting focusout persists the cancelled order.
      this.#release(cell, true);
      this.#restore();
      this.#say(this.dataset.cancelledText, cell);
      handle.focus();
      return;
    }

    if (this.#grabbed !== cell) return;

    const delta = { ArrowUp: -1, ArrowLeft: -1, ArrowDown: 1, ArrowRight: 1 }[event.key];
    if (!delta) return;

    event.preventDefault();
    if (this.#move(cell, delta)) {
      this.#sync();
      handle.focus();
      this.#say(this.dataset.movedText, cell);
    }
  };

  #onFocusOut = (event) => {
    const cell = this.#grabbed;
    if (this.#moving || !cell || cell.contains(event.relatedTarget)) return;
    this.#release(cell);
  };

  #grab(cell, handle) {
    this.#markSnapshot();
    this.#grabbed = cell;
    cell.classList.add('is-grabbed');
    handle.setAttribute('aria-pressed', 'true');
    this.#say(this.dataset.grabbedText, cell);
  }

  #release(cell, silent = false) {
    this.#grabbed = null;
    cell.classList.remove('is-grabbed');
    cell.querySelector(HANDLE)?.setAttribute('aria-pressed', 'false');
    if (silent) return;

    this.#say(this.dataset.droppedText, cell);
    void this.#persist();
  }

  #restore() {
    const list = this.#list;
    if (!list || !this.#snapshot) return;

    const byKey = new Map(this.#cells().map((cell) => [cell.dataset.wishlistItem, cell]));
    this.#moving = true;
    for (const key of this.#snapshot) {
      const cell = byKey.get(key);
      if (cell) list.append(cell);
    }
    this.#moving = false;
    this.#sync();
  }

  async #persist() {
    // Serialised on one in-flight promise: concurrent writes let arrival order decide the result.
    this.#persisting = this.#persisting
      .catch(() => {})
      .then(() => this.#persistOnce());
    return this.#persisting;
  }

  async #persistOnce() {
    const keys = this.#keys();
    if (!this.#snapshot || String(this.#snapshot) === String(keys)) return;

    const previous = this.#snapshot;
    this.#snapshot = keys;
    // Held for the whole write: a failure must roll back to the last SERVER-confirmed order.
    this.#persistBusy = true;

    try {
      if (isLoggedIn()) await reorder(keys, this.dataset.listId || undefined);
      else if (!reorderGuest(keys)) throw new Error('guest_write_failed');
    } catch {
      if (!this.isConnected) return;
      this.#snapshot = previous;
      this.#restore();
      this.#snapshot = this.#keys();
      // Through #say, which clears first — a second failed reorder is otherwise silent.
      this.#say(this.dataset.errorText, null);
    } finally {
      this.#persistBusy = false;
    }
  }
}

if (!customElements.get('wishlist-sortable')) {
  customElements.define('wishlist-sortable', WishlistSortable);
}
